import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { CurrentUser } from '@/services/api/current-user'
import { LoadSessionData } from '@/services/session/load-session-data'
import { EveryPageViewEvent } from '@/usecases/event-bus-for-third-party/event'
import { Either, left, right } from 'fp-ts/lib/Either'

export interface EveryPageViewEventGateway {
  getEveryPageViewEvent(): Promise<Either<Error, EveryPageViewEvent>>
}

/**
 * 有料会員機能がOFFの場合は有料会員情報は空とし、userIdはセッションデータから取得する
 */
export class PaidMembershipDisabledEveryPageViewEventGateway implements EveryPageViewEventGateway {
  constructor(
    private readonly loadSessionDataInstance: LoadSessionData,
    private readonly adCode: string | undefined
  ) {}

  async getEveryPageViewEvent(): Promise<Either<Error, EveryPageViewEvent>> {
    return this.loadSessionDataInstance.load().then(
      payload => right({
        userId: payload.data.userId,
        adCode: this.adCode,
        paidMembership: undefined,
      }),
      error => left(error) as Either<Error, EveryPageViewEvent>
    )
  }
}

/**
 * 有料会員機能がONの場合は有料会員情報、ユーザーIDともにcurrent_userで取得する
 */
export class PaidMembershipEnabledEveryPageViewEventGateway implements EveryPageViewEventGateway {
  constructor(
    private readonly currentUser: CurrentUser,
    private readonly adCode: string | undefined
  ) {}

  async getEveryPageViewEvent(): Promise<Either<Error, EveryPageViewEvent>> {
    return this.currentUser.getCurrentUser().then(result => result.fold(
      error => left(error),
      user => right({
        userId: user.id,
        adCode: this.adCode,
        paidMembership: user.membership,
      })
    ))
  }
}

export class EveryPageViewEventGatewayFactory {
  constructor(
    private readonly currentUser: CurrentUser = new CurrentUser(),
    private readonly loadSessionDataInstance: LoadSessionData = LoadSessionData.getInstance()
  ) {}

  create(behavior: BehaviorConfig, adCode: string | undefined): EveryPageViewEventGateway {
    return behavior.paidMembership.enabled
      ? new PaidMembershipEnabledEveryPageViewEventGateway(this.currentUser, adCode)
      : new PaidMembershipDisabledEveryPageViewEventGateway(this.loadSessionDataInstance, adCode)
  }
}
